import { Line } from '@react-three/drei'
import { useMemo } from 'react'
import { DoubleSide } from 'three'
import { Rotulo3D } from '../componentes/Rotulo3D'

export interface AnelZona {
  id: string
  raioM: number
  cor: string
  /** Preenche o disco (zonas); sem preenchimento fica só o contorno (limiares de referência). */
  preenchido?: boolean
  opacidade?: number
  tracejado?: boolean
  rotulo?: string
  subrotulo?: string
  varianteRotulo?: 'quente' | 'morna' | 'fria' | 'zona' | 'neutro' | 'risco'
  /** Direção do rótulo no anel (rad, a partir de +Z para +X); sem isto, distribui automaticamente. */
  anguloRotuloRad?: number
}

const SEGMENTOS = 160
const nenhumRaio = () => null

/**
 * Anéis concêntricos no chão, em torno da origem do grupo.
 *
 * Os discos preenchidos vão do maior para o menor, um por cima do outro, com
 * `polygonOffset` crescente: assim a ZQ pinta por cima da ZM sem z-fighting e
 * sem precisar recortar coroas.
 *
 * Os rótulos vão para a lateral direita da vista padrão (a câmera olha de +X+Z)
 * e cada anel desloca um pouco o seu, para que anéis próximos não empilhem os
 * rótulos nem caiam atrás da barra de reprodução na borda de baixo.
 */
export function AneisZona3D({ aneis, anguloRotulosRad = (3 * Math.PI) / 4 }: { aneis: readonly AnelZona[]; anguloRotulosRad?: number }) {
  const ordenados = useMemo(() => [...aneis].sort((a, b) => b.raioM - a.raioM), [aneis])
  return (
    <group>
      {ordenados.map((anel, indice) => (
        <group key={anel.id}>
          {anel.preenchido && (
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} raycast={nenhumRaio} renderOrder={indice}>
              <circleGeometry args={[anel.raioM, SEGMENTOS]} />
              <meshBasicMaterial
                color={anel.cor}
                transparent
                opacity={anel.opacidade ?? 0.16}
                depthWrite={false}
                side={DoubleSide}
                polygonOffset
                polygonOffsetFactor={-4 - indice}
                polygonOffsetUnits={-4 - indice}
              />
            </mesh>
          )}
          <Contorno raioM={anel.raioM} cor={anel.cor} tracejado={anel.tracejado} />
          {anel.rotulo && (
            <Rotulo3D
              posicao={[
                Math.sin(anel.anguloRotuloRad ?? anguloRotulosRad - indice * 0.4) * anel.raioM,
                2,
                Math.cos(anel.anguloRotuloRad ?? anguloRotulosRad - indice * 0.4) * anel.raioM,
              ]}
              texto={anel.rotulo}
              subtexto={anel.subrotulo}
              variante={anel.varianteRotulo ?? 'zona'}
              fator={Math.max(40, anel.raioM * 0.35)}
            />
          )}
        </group>
      ))}
    </group>
  )
}

function Contorno({ raioM, cor, tracejado }: { raioM: number; cor: string; tracejado?: boolean }) {
  const pontos = useMemo(
    () =>
      Array.from({ length: SEGMENTOS + 1 }, (_, i) => {
        const a = (i / SEGMENTOS) * Math.PI * 2
        return [Math.sin(a) * raioM, 0.06, Math.cos(a) * raioM] as [number, number, number]
      }),
    [raioM],
  )
  return (
    <Line
      points={pontos}
      color={cor}
      lineWidth={2.2}
      dashed={tracejado}
      dashSize={raioM * 0.03}
      gapSize={raioM * 0.02}
      raycast={nenhumRaio}
    />
  )
}
